export interface MinutesRange {
  start: number;
  end: number;
}

const MINUTES_IN_DAY = 1440;

const isMinute = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= MINUTES_IN_DAY;

/** A plugin output that looks like a minutes-from-midnight interval — rendered on the 24h timeline. */
export const isMinutesRange = (v: unknown): v is MinutesRange => {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const { start, end } = v as Record<string, unknown>;
  return isMinute(start) && isMinute(end);
};

/** The shared timeline-range shape (expected_ranges / actual_ranges): same interval, spelled startMinutes/endMinutes. */
const isTimelineRange = (v: unknown): v is { startMinutes: number; endMinutes: number } => {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const { startMinutes, endMinutes } = v as Record<string, unknown>;
  return isMinute(startMinutes) && isMinute(endMinutes);
};

/** Normalises either interval spelling to MinutesRange, or null when the value is neither. */
export const toMinutesRange = (v: unknown): MinutesRange | null => (isMinutesRange(v) ? v : isTimelineRange(v) ? { start: v.startMinutes, end: v.endMinutes } : null);

export const toMinutesRangeArray = (v: unknown): MinutesRange[] | null => {
  if (!Array.isArray(v) || v.length === 0) return null;
  const ranges = v.map(toMinutesRange);
  return ranges.every((r): r is MinutesRange => r !== null) ? ranges : null;
};

export const isMinutesRangeArray = (v: unknown): v is MinutesRange[] => Array.isArray(v) && v.length > 0 && v.every(isMinutesRange);

export const isScalar = (v: unknown): v is string | number | boolean | null => v === null || ['string', 'number', 'boolean'].includes(typeof v);
